import { Link } from '@tanstack/react-router';
import { T, useGT } from 'gt-react';
import { Button } from '@/components/ui/button';
import type { NotificationItem as NotificationItemType } from '@erp/rpc';
import {
  getNotificationRenderer,
  type NotificationLevel,
} from './notificationRenderers';
import {
  NotificationActionButtons,
  extractActions,
} from './NotificationActions';
import { cn } from '@/lib/utils';

/**
 * Tailwind palette for the avatar circle + body accent, keyed by
 * notification severity. Kept in one place so popover and history page
 * stay visually consistent.
 *
 * Falls back to neutral muted-gray when no level is present (most event
 * types don't set one).
 */
const LEVEL_STYLES: Record<
  NotificationLevel,
  { avatar: string; leftBorder: string }
> = {
  info: {
    avatar: 'bg-blue-100 text-blue-700',
    leftBorder: 'border-l-2 border-l-blue-400',
  },
  warn: {
    avatar: 'bg-amber-100 text-amber-700',
    leftBorder: 'border-l-2 border-l-amber-400',
  },
  critical: {
    avatar: 'bg-destructive/15 text-destructive',
    leftBorder: 'border-l-2 border-l-destructive',
  },
};

interface Props {
  notification: NotificationItemType;
  onMarkAsRead: (id: string) => void;
  onArchive: (id: string) => void;
  /** When provided, an Unarchive action is shown instead of Dismiss. */
  onUnarchive?: (id: string) => void;
}

export function NotificationItem({
  notification,
  onMarkAsRead,
  onArchive,
  onUnarchive,
}: Props) {
  const gt = useGT();
  const renderer = getNotificationRenderer(notification.eventType);
  const link = renderer.getLink(
    notification.notifiableType,
    notification.notifiableId,
  );
  const isUnread = notification.readAt === null;

  // Resolve per-payload icon + level (e.g. SystemAnnouncement swaps the
  // glyph based on payload.level). Falls back to the renderer's default.
  const Icon = renderer.getIcon?.(notification.payload) ?? renderer.Icon;
  const lvl: NotificationLevel | null =
    renderer.getLevel?.(notification.payload) ?? null;
  const levelStyle = lvl ? LEVEL_STYLES[lvl] : null;
  const actions = extractActions(notification.payload);

  // The icon, title, and body are all renderer-provided so the row layout
  // stays generic. Clicking the body navigates and marks as read; archive
  // is a separate explicit action so users don't lose context by accident.
  const content = (
    <div className="flex items-start gap-3 px-3 py-3 w-full">
      <div
        className={cn(
          'mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
          // Level color wins when present so severity reads first; read/unread
          // distinction still comes through via the font weight + side dot.
          levelStyle
            ? levelStyle.avatar
            : isUnread
              ? 'bg-primary/10 text-primary'
              : 'bg-muted text-muted-foreground',
        )}
      >
        <Icon className="h-4 w-4" weight="regular" />
      </div>
      <div className="min-w-0 flex-1">
        <div
          className={cn('text-sm', isUnread ? 'font-medium' : 'font-normal')}
        >
          {renderer.getTitle(notification.payload)}
        </div>
        <div className="text-xs text-muted-foreground line-clamp-2">
          {renderer.getBody(notification.payload)}
        </div>
        <div className="mt-1 text-[11px] text-muted-foreground">
          {new Date(notification.createdAt).toLocaleString()}
        </div>
      </div>
    </div>
  );

  return (
    <div
      className={cn(
        'group relative hover:bg-accent/40',
        // Read rows fade into a muted background so unread items pop
        // by contrast. Unread rows keep the clean card surface.
        !isUnread && 'bg-muted/40',
        // Severity color wins for the left accent; when there's no
        // severity we still mark unread items with a primary border so
        // the read/unread distinction reads without the right-side dot
        // (which used to collide with the Dismiss button on hover).
        levelStyle?.leftBorder ??
          (isUnread ? 'border-l-2 border-l-primary' : null),
      )}
      aria-label={
        isUnread
          ? gt('unread notification', {
              context: 'aria label denoting an unread notification row',
            })
          : undefined
      }
    >
      {link ? (
        <Link
          to={link}
          onClick={() => isUnread && onMarkAsRead(notification.id)}
          className="block"
        >
          {content}
        </Link>
      ) : (
        <button
          type="button"
          onClick={() => isUnread && onMarkAsRead(notification.id)}
          className="block w-full text-left"
        >
          {content}
        </button>
      )}
      {actions.length > 0 && (
        <NotificationActionButtons
          actions={actions}
          className="mb-2 ml-14 mr-3 flex flex-wrap gap-1.5"
        />
      )}
      <div className="absolute right-2 top-2 opacity-0 transition-opacity group-hover:opacity-100">
        {notification.archivedAt && onUnarchive ? (
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-xs"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onUnarchive(notification.id);
            }}
          >
            <T context="restore an archived notification">Restore</T>
          </Button>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-xs"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onArchive(notification.id);
            }}
          >
            <T context="dismiss a notification">Dismiss</T>
          </Button>
        )}
      </div>
    </div>
  );
}
